import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastManualInteraction, setLastManualInteraction] = useState(0);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToIndex = (index: number) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardWidth = 320; // w-80 = 320px
      const gap = 24; // gap-6 = 24px
      const scrollPosition = index * (cardWidth + gap);
      
      console.log(`Scrolling to index ${index}, position: ${scrollPosition}`);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      
      setCurrentIndex(index);
      setLastManualInteraction(Date.now());
      setIsAutoScrolling(false);
      
      // Clear existing interval and restart auto-scroll after delay
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      setTimeout(() => {
        setIsAutoScrolling(true);
      }, 5000);
    }
  };

  const scrollLeft = () => {
    console.log('Left arrow clicked, current index:', currentIndex);
    const newIndex = currentIndex > 0 ? currentIndex - 1 : testimonials.length - 1;
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    console.log('Right arrow clicked, current index:', currentIndex);
    const newIndex = currentIndex < testimonials.length - 1 ? currentIndex + 1 : 0;
    scrollToIndex(newIndex);
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const startAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      
      autoScrollIntervalRef.current = setInterval(() => {
        if (isAutoScrolling) {
          const timeSinceLastInteraction = Date.now() - lastManualInteraction;
          if (timeSinceLastInteraction > 5000) { // 5 seconds delay
            setCurrentIndex(prevIndex => {
              const nextIndex = prevIndex < testimonials.length - 1 ? prevIndex + 1 : 0;
              
              // Scroll to the next testimonial
              const cardWidth = 320;
              const gap = 24;
              const scrollPosition = nextIndex * (cardWidth + gap);
              
              scrollContainer.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
              });
              
              return nextIndex;
            });
          }
        }
      }, 4000); // Check every 4 seconds
    };

    if (isAutoScrolling) {
      startAutoScroll();
    }

    // Pause on hover
    const handleMouseEnter = () => {
      setIsAutoScrolling(false);
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };

    const handleMouseLeave = () => {
      setLastManualInteraction(Date.now());
      setTimeout(() => {
        setIsAutoScrolling(true);
      }, 2000);
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [isAutoScrolling]); // Only depend on isAutoScrolling

  const testimonials = [
    {
      name: "Priya Sharma",
      testimonial: "Dailzy has been a lifesaver! I can order groceries during my lunch break and they're delivered before I even reach home. The app is so easy to use.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b176?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80",
      location: "Mumbai"
    },
    {
      name: "Rajesh Kumar",
      testimonial: "The 10-minute delivery promise seemed too good to be true, but they actually deliver! Perfect for those late-night coding sessions when I need snacks.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80",
      location: "Bangalore"
    },
    {
      name: "Aisha Patel",
      testimonial: "As a student, I love the affordable prices and quick delivery. The app even suggests products based on my previous orders. Super convenient!",
      rating: 4,
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      location: "Delhi"
    },
    {
      name: "Vikram Singh",
      testimonial: "I run a small café and often need ingredients urgently. Dailzy saves me multiple trips to the market. Their vendor network is impressive!",
      rating: 5,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      location: "Pune"
    },
    {
      name: "Meera Gupta",
      testimonial: "My daughter introduced me to Dailzy. The delivery boys are so polite and helpful. I can get my medicines and groceries without stepping out.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      location: "Chennai"
    },
    {
      name: "Arjun Mehta",
      testimonial: "Perfect for my pre-workout snacks and protein supplements. The variety of healthy options available is amazing. Quick delivery never disappoints!",
      rating: 4,
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80",
      location: "Hyderabad"
    }
  ];

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={16}
        className={`${
          index < rating
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-red-50 relative overflow-hidden">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {/* Background decorations */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-r from-red-200 to-orange-200 rounded-full opacity-20 animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-24 h-24 bg-gradient-to-r from-orange-200 to-red-200 rounded-full opacity-30 animate-pulse delay-1000"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Quote size={20} />
            <span className="font-semibold text-sm">Customer Stories</span>
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            What Our <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Customers Say</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join thousands of satisfied customers who trust Dailzy for their daily needs. Real stories from real people.
          </p>
        </motion.div>

        {/* Testimonials container with arrows */}
        <div className="relative">
          {/* Left arrow */}
          <motion.button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Left arrow clicked');
              scrollLeft();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white rounded-full shadow-xl border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-orange-500 hover:border-red-300 transition-all duration-300 hover:shadow-2xl active:scale-95 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <ChevronLeft className="w-7 h-7" />
          </motion.button>

          {/* Right arrow */}
          <motion.button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Right arrow clicked');
              scrollRight();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white rounded-full shadow-xl border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-orange-500 hover:border-red-300 transition-all duration-300 hover:shadow-2xl active:scale-95 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <ChevronRight className="w-7 h-7" />
          </motion.button>

          {/* Horizontal scrollable testimonials */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 hide-scrollbar mx-16" 
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none'
            }}
          >
          {/* Show only original testimonials for proper navigation */}
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group flex-shrink-0 w-80"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Quote icon */}
              <div className="flex justify-end mb-4">
                <Quote size={24} className="text-red-200 group-hover:text-red-300 transition-colors" />
              </div>
              
              {/* Testimonial text */}
              <p className="text-gray-600 mb-6 leading-relaxed italic">
                "{testimonial.testimonial}"
              </p>
              
              {/* User info */}
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-red-100 group-hover:ring-red-200 transition-all"
                  />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">{testimonial.name}</h4>
                  {/* Rating below name */}
                  <div className="flex items-center space-x-1 mb-1">
                    {renderStars(testimonial.rating)}
                  </div>
                  <p className="text-xs text-red-500 font-medium">{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
          </div>
          
          {/* Navigation dots indicator */}
          <div className="flex justify-center items-center space-x-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`rounded-full transition-all duration-300 hover:scale-110 ${
                  currentIndex === index 
                    ? 'bg-red-500 w-8 h-3 shadow-lg' 
                    : 'bg-gray-300 hover:bg-gray-400 w-3 h-3'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Trust indicators */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-500 mb-2">50K+</div>
              <div className="text-sm text-gray-600">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-500 mb-2">4.8★</div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">98%</div>
              <div className="text-sm text-gray-600">On-Time Delivery</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">Customer Support</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials; 